import React, { useEffect, useContext, useState } from 'react';
import { SpaceBetween } from '@amzn/awsui-components-react/polaris';
import { DashboardState, initialState } from 'src/models/dashboard/DashboardState';
import { dashboardReducer, ACTIONS } from 'src/services/dashboard/DashboardReducer';
import './Dashboard.scss';
import { GlobalAppContext } from 'src/components/App';
import useReducerWithLogger from 'src/services/utils/ReducerWithLogger';
import DashboardTiles from './DashboardTiles';
import { getTopOpenCalcReviewsSearchPayload } from './helpers/OpenCalculationReviewsSearchHelper';
import OpenApprovalsAndRequestsContainer from './OpenApprovalsAndRequestsContainer';
import { getTopOpenApprovalsSearchPayload } from './helpers/ApprovalsSearchHelper';
import { getTopOpenRequestsSearchPayload } from './helpers/RequestsSearchHelper';
import OpenCalculationReviewsContainer from './OpenCalculationReviewsContainer';
import TPEErrorWatcher from '../shared/TPEErrorWatcher';
import NotificationSummaryView from './NotificationSummaryView';
import { getTopOpenTaksSearchPayload } from './helpers/TasksSearchHelper';
import OpenTasksContainer from './OpenTasksContainer';
import TPEAction from 'src/models/common/TPEAction';
import CONSTANTS from 'src/utils/constants';
import MarkNotificationsComplete from '../shared/MarkNotificationsComplete';

export type ContextType = {
    dashboardState: DashboardState,
    dashboardDispatch: React.Dispatch<TPEAction>
}

const DashboardProvider = (props: any) => {
    const { state, dispatch, children } = props;
    const providerValue = React.useMemo(() => ({
        dashboardState: state, dashboardDispatch: dispatch
    }), [state, dispatch]);
    return (
        <DashboardContext.Provider value={providerValue}>
            {children}
        </DashboardContext.Provider>
    );
}

export const DashboardContext = React.createContext(null as unknown as ContextType);

export default function DashboardView() {
    const [state, dispatch] = useReducerWithLogger(dashboardReducer, initialState);
    const { userProfile, services } = useContext(GlobalAppContext);

    const { searchOpenCalcReviewsPayload, 
            searchOpenApprovalsPayload, 
            searchOpenRequestsPayload, 
            searchOpenTasksPayload,
            selectedNotifications } = state;

    const [ loadTime, setLoadTime ] = useState('');
    const [ triggerMarkComplete, setTriggerMarkComplete ] = useState('');

    const [openCalculationReviewsResult, openCalculationReviewsLoading, searchOpenCalcReviewsError] = services.dashboardService.searchOpenCalculationReviews(searchOpenCalcReviewsPayload);
    const [openApprovalsResult, openApprovalsLoading, searchOpenApprovalsError] = services.dashboardService.searchApprovals(searchOpenApprovalsPayload);
    const [openRequestsResult, openRequestsLoading, searchOpenRequestsError] = services.dashboardService.searchRequests(searchOpenRequestsPayload);
    const [openTasksResult, openTasksLoading, searchOpenTasksError] = services.dashboardService.searchTasks(searchOpenTasksPayload);
    const [notificationSummaryResult, notificationSummaryLoading, notificationSummaryError] = services.dashboardService.getNotificationSummary(loadTime);

    const postProcessAfterMarkComplete = () => {
        dispatch(ACTIONS.SET_OPEN_TASKS_PAYLOAD.withPayload(getTopOpenTaksSearchPayload()));
        setLoadTime(new Date().toString());
    }

    useEffect(() => {
        setLoadTime(new Date().toString());
        dispatch(ACTIONS.SET_OPEN_CALC_REVIEWS_PAYLOAD.withPayload(getTopOpenCalcReviewsSearchPayload()));
        dispatch(ACTIONS.SET_OPEN_APPROVALS_PAYLOAD.withPayload(getTopOpenApprovalsSearchPayload()));
        dispatch(ACTIONS.SET_OPEN_REQUESTS_PAYLOAD.withPayload(getTopOpenRequestsSearchPayload()));
        dispatch(ACTIONS.SET_OPEN_TASKS_PAYLOAD.withPayload(getTopOpenTaksSearchPayload()));
    }, [])

    return (
       <DashboardProvider state={state} dispatch={dispatch}>
            <div style={{ marginLeft: '50px', marginRight:'50px' }}>
                <div className="welcomeUser">Welcome {userProfile?.firstName}!</div>
                <SpaceBetween direction="vertical" size="l">
                    <NotificationSummaryView {... {notificationSummaryResult, notificationSummaryLoading}}/>
                    <OpenApprovalsAndRequestsContainer {... {openApprovalsResult, openApprovalsLoading, openRequestsResult, openRequestsLoading}}/>
                    {/* Hide tasks in production environment */}
                    {CONSTANTS.ENVIRONMENT_VARIABLES.TPE_UI_STAGE !== "prod" && <OpenTasksContainer {... {openTasksResult, openTasksLoading, onMarkComplete: () => setTriggerMarkComplete(new Date().toString())}} />}
                    <OpenCalculationReviewsContainer {... {openCalculationReviewsResult, openCalculationReviewsLoading}} />
                    <DashboardTiles />
                </SpaceBetween>
                <MarkNotificationsComplete triggerAction={triggerMarkComplete} selectedNotifications={selectedNotifications} postProcess={postProcessAfterMarkComplete}/>
                <TPEErrorWatcher services={services} errors={[searchOpenCalcReviewsError, searchOpenApprovalsError, 
                    searchOpenRequestsError, notificationSummaryError, searchOpenTasksError]} />
            </div>
        </DashboardProvider>
    );
}